import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconSearch } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Group,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";

const ClientPaymentsTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [payments, setPayments] = useState([]);
  const [conflicts, setConflicts] = useState([]);

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const loadSkippedDuplicateConflicts = async () => {
    if (!form?.values?._id) {
      setConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(
        form.values._id,
      );
      if (result?.success) {
        setConflicts(
          result.data?.excludedConflicts ||
            result.data?.skippedDuplicateSources ||
            result.data?.conflicts ||
            [],
        );
      }
    } catch (error) {
      console.log("loadSkippedDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    loadSkippedDuplicateConflicts();
  }, [form?.values?._id]);

  useEffect(() => {
    const loadSavedClientPayments = async () => {
      const savedSearch = form?.values?.clientPaymentSearch;
      if (savedSearch) {
        if (savedSearch.fromDate) setFromDate(new Date(savedSearch.fromDate));
        if (savedSearch.toDate) setToDate(new Date(savedSearch.toDate));
      }

      const savedClientPaymentIds = form?.values?.clientPayments;
      if (!savedClientPaymentIds || savedClientPaymentIds.length === 0) return;

      try {
        setLoading(true);
        const result = await api.Payroll2.getClientPaymentsByIds(
          savedClientPaymentIds,
        );
        if (result?.success && result.data?.payments) {
          setPayments(result.data.payments);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedClientPayments();
  }, [form?.values?._id]);

  const saveClientPaymentsToPayroll = async (paymentIds) => {
    if (!form?.values?._id || !form?.updateAndSave) return;
    try {
      await form.updateAndSave({
        clientPayments: paymentIds,
        clientPaymentSearch: {
          fromDate,
          toDate,
        },
      });
    } catch (error) {
      console.log("Error saving client payments to payroll:", error);
    }
  };

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true);
      const result = await api.Payroll2.getClientPaymentsForPayroll(
        moment(fromDate).format("YYYY-MM-DD"),
        moment(toDate).format("YYYY-MM-DD"),
        form?.values?._id,
      );

      if (result && result.success && result.data && result.data.payments) {
        setPayments(result.data.payments);
        setConflicts(result.data.excludedConflicts || result.data.conflicts || []);
        const paymentIds = result.data.payments.map((p) => p._id);
        await saveClientPaymentsToPayroll(paymentIds);
        console.log(
          `Loaded ${result.data.count} client payments (no pagination limit)`,
        );
      } else {
        setPayments([]);
        setConflicts([]);
      }
    } catch (error) {
      console.log("Error fetching client payments:", error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "MATCHED":
        return "green";
      case "MATCHING":
        return "yellow";
      case "VOID":
        return "red";
      case "REFUND":
        return "orange";
      case "DIRECT":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Search Client Payments
        </Title>

        <Group align="flex-end" spacing="md">
          <DatePicker
            label="From Date"
            size="xs"
            placeholder="Select start date"
            value={fromDate}
            onChange={setFromDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <DatePicker
            label="To Date"
            size="xs"
            placeholder="Select end date"
            value={toDate}
            onChange={setToDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <Button
            size="xs"
            leftIcon={<IconSearch size={14} />}
            onClick={handleSearch}
            disabled={!fromDate || !toDate || isConfirmed}
          >
            Search
          </Button>
        </Group>

        <Text size="xs" color="dimmed" mt="xs">
          Only the client payments matched within the dates will be filtered
        </Text>

        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      <Box mb="md">
        <DuplicateBidConflictsAlert conflicts={conflicts} />
      </Box>

      {payments.length > 0 && (
        <Paper p="md" withBorder>
          <Group position="apart" mb="md">
            <Title order={5}>Results</Title>
            <Badge size="lg">{payments.length} payments found</Badge>
          </Group>

          <Accordion variant="separated">
            {payments.map((payment, index) => (
              <Accordion.Item key={payment._id} value={payment._id}>
                <Accordion.Control>
                  <Group position="apart" noWrap>
                    <Group spacing="xl" noWrap>
                      <Badge
                        size="sm"
                        variant="filled"
                        color="dark"
                        sx={{ minWidth: 36, flexShrink: 0 }}
                      >
                        {index + 1}
                      </Badge>

                      <Box w={110}>
                        <Text size="sm" weight={600}>
                          {payment.code}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {payment.date
                            ? moment(payment.date).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>

                      <Box w={550}>
                        <Text size="sm" weight={500} lineClamp={1}>
                          {payment.debitNotes && payment.debitNotes.length > 0
                            ? payment.debitNotes
                                .map((dn) => dn.code || dn)
                                .join(" / ")
                            : "-"}
                        </Text>
                        <Text size="xs" color="dimmed" lineClamp={1}>
                          {payment.debitNotes?.[0]?.client?.code ||
                            payment.fromClient?.code ||
                            "-"}{" "}
                          -{" "}
                          {payment.debitNotes?.[0]?.client?.name ||
                            payment.fromClient?.name ||
                            "-"}
                        </Text>
                      </Box>

                      <Box w={120}>
                        <Text size="sm" weight={500}>
                          {formatter.currency(payment.amount)}
                        </Text>
                      </Box>

                      <Box w={100}>
                        <Text size="sm">
                          {payment.matchedDate
                            ? moment(payment.matchedDate).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Matched Date
                        </Text>
                      </Box>
                    </Group>

                    <Badge color={getStatusColor(payment.status)} size="sm">
                      {payment.status}
                    </Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack spacing="md" mt="md">
                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">
                          Payment Code
                        </Text>
                        <Text size="sm" weight={500}>
                          {payment.code}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Payment Date
                        </Text>
                        <Text size="sm">
                          {payment.date
                            ? moment(payment.date).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Matched Date
                        </Text>
                        <Text size="sm">
                          {payment.matchedDate
                            ? moment(payment.matchedDate).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Created Date
                        </Text>
                        <Text size="sm">
                          {payment.createdAt
                            ? moment(payment.createdAt).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Amount
                        </Text>
                        <Text size="sm" weight={500}>
                          {formatter.currency(payment.amount)}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Payment Method
                        </Text>
                        <Text size="sm">{payment.paymentMethod || "-"}</Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Status
                        </Text>
                        <Badge color={getStatusColor(payment.status)} size="sm">
                          {payment.status}
                        </Badge>
                      </Box>
                    </Group>

                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">
                          Bank Account
                        </Text>
                        <Text size="sm">
                          {payment.bankAccount?.accountNumber || "-"}
                        </Text>
                      </Box>
                      {payment.chequeNumber && (
                        <Box>
                          <Text size="xs" color="dimmed">
                            Cheque Number
                          </Text>
                          <Text size="sm">{payment.chequeNumber}</Text>
                        </Box>
                      )}
                      {payment.remark && (
                        <Box>
                          <Text size="xs" color="dimmed">
                            Remark
                          </Text>
                          <Text size="sm">{payment.remark}</Text>
                        </Box>
                      )}
                    </Group>

                    {payment.debitNoteAssignments &&
                      payment.debitNoteAssignments.length > 0 && (
                        <>
                          <Text size="sm" weight={500}>
                            Debit Note Assignments:
                          </Text>
                          <Table fontSize="xs" striped highlightOnHover>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>DN Code</th>
                                <th>Class</th>
                                <th>DN Period</th>
                                <th>Client</th>
                                <th>Payment Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payment.debitNoteAssignments.map(
                                (assignment, idx) => (
                                  <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>
                                      <EntityTabAnchor
                                        entity="debitNote"
                                        id={
                                          assignment.debitNote?._id ||
                                          assignment.debitNote
                                        }
                                        label={
                                          assignment.debitNote?.code ||
                                          assignment.debitNote
                                        }
                                      />
                                    </td>
                                    <td>
                                      {assignment.debitNote?.productType
                                        ?.name || "-"}
                                    </td>
                                    <td>
                                      {assignment.debitNote?.period?.start
                                        ? `${moment(
                                            assignment.debitNote.period.start,
                                          ).format("YYYY-MM-DD")} ~ ${
                                            assignment.debitNote.period?.end
                                              ? moment(
                                                  assignment.debitNote.period
                                                    .end,
                                                ).format("YYYY-MM-DD")
                                              : "-"
                                          }`
                                        : "-"}
                                    </td>
                                    <td>
                                      {assignment.debitNote?.client?.code ||
                                        "-"}{" "}
                                      {assignment.debitNote?.client?.name &&
                                        `- ${assignment.debitNote.client.name}`}
                                    </td>
                                    <td>
                                      {formatter.currency(
                                        assignment.paymentAmount,
                                      )}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}

      {payments.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No payments found. Please enter date range and click Search.
          </Text>
        </Paper>
      )}
    </Box>
  );
};

export default ClientPaymentsTab;
